import { useState, useMemo } from 'react';
import type { SearchDocumentResult, SearchDocumentsInput } from '@erp/rpc';
import { useDocumentSearch } from '@/api/rpc/hooks/accounts-payable/useDocumentSearch';
import { useDebounce } from '@/hooks/useDebounce';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
// TODO: cn utility may be used later for styling variations
import { formatDateOnlyString } from '@/utils/dateUtils';
import {
  MagnifyingGlassIcon,
  CurrencyDollarIcon,
  CaretLeftIcon,
  CaretRightIcon,
  XIcon,
  FunnelIcon,
} from '@phosphor-icons/react';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';

// Status labels and variants for POs
const PO_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending',
  APPROVED: 'Approved',
  SENT: 'Sent',
  PARTIALLY_RECEIVED: 'Part. Received',
  FULLY_RECEIVED: 'Received',
  PARTIALLY_INVOICED: 'Part. Invoiced',
  FULLY_INVOICED: 'Invoiced',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

const PO_STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  DRAFT: 'outline',
  PENDING_APPROVAL: 'secondary',
  APPROVED: 'default',
  SENT: 'default',
  PARTIALLY_RECEIVED: 'secondary',
  FULLY_RECEIVED: 'default',
  PARTIALLY_INVOICED: 'secondary',
  FULLY_INVOICED: 'outline',
  CLOSED: 'outline',
  CANCELLED: 'destructive',
};

// Status labels and variants for SOs
const SO_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  fulfilled: 'Fulfilled',
  closed: 'Closed',
};

const SO_STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  pending: 'secondary',
  in_progress: 'default',
  fulfilled: 'default',
  closed: 'outline',
};

const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return '—';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

export interface AdvancedDocumentSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Invoice ID for context-aware ranking */
  invoiceId?: string;
  /** Supplier ID to filter POs (from invoice) */
  supplierId?: string;
  /** Customer ID to filter SOs */
  customerId?: string;
  /** Invoice date for relevance ranking */
  invoiceDate?: string;
  /** Invoice amount for relevance ranking */
  invoiceAmount?: string;
  /** Currency for display */
  currency?: string;
  /** Document IDs to exclude from results */
  excludeDocumentIds?: string[];
  /** Callback when a document is selected */
  onSelect: (document: SearchDocumentResult) => void;
}

type DocumentTypeFilter = 'ALL' | 'PURCHASE_ORDER' | 'SALES_ORDER';

export function AdvancedDocumentSearch({
  open,
  onOpenChange,
  invoiceId,
  supplierId: initialSupplierId,
  customerId: initialCustomerId,
  invoiceDate,
  invoiceAmount,
  currency = 'USD',
  excludeDocumentIds,
  onSelect,
}: AdvancedDocumentSearchProps) {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] =
    useState<DocumentTypeFilter>('ALL');
  const [selectedSupplierId, setSelectedSupplierId] = useState<
    string | undefined
  >(initialSupplierId);
  const [selectedCustomerId, setSelectedCustomerId] = useState<
    string | undefined
  >(initialCustomerId);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const pageSize = 15;

  const debouncedQuery = useDebounce(searchQuery, 300);
  const excludeSet = useMemo(
    () => new Set(excludeDocumentIds ?? []),
    [excludeDocumentIds],
  );

  // Build search input
  const searchInput: SearchDocumentsInput = useMemo(() => {
    const mode =
      documentTypeFilter === 'PURCHASE_ORDER'
        ? ('PURCHASE_ORDERS_ONLY' as const)
        : documentTypeFilter === 'SALES_ORDER'
          ? ('SALES_ORDERS_ONLY' as const)
          : ('ALL' as const);

    return {
      query: debouncedQuery.length > 0 ? debouncedQuery : undefined,
      mode,
      invoiceId,
      supplierId:
        documentTypeFilter !== 'SALES_ORDER' && selectedSupplierId
          ? selectedSupplierId
          : undefined,
      customerId:
        documentTypeFilter !== 'PURCHASE_ORDER' && selectedCustomerId
          ? selectedCustomerId
          : undefined,
      invoiceDate,
      invoiceAmount,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      amountMin: amountMin || undefined,
      amountMax: amountMax || undefined,
      limit: pageSize,
      offset: page * pageSize,
    };
  }, [
    debouncedQuery,
    documentTypeFilter,
    invoiceId,
    selectedSupplierId,
    selectedCustomerId,
    invoiceDate,
    invoiceAmount,
    dateFrom,
    dateTo,
    amountMin,
    amountMax,
    page,
  ]);

  const query = useDocumentSearch(searchInput, open);

  const results = useMemo(() => {
    return query.results.filter((doc) => !excludeSet.has(doc.id));
  }, [query.results, excludeSet]);

  const totalPages = Math.ceil(query.pagination.total / pageSize);

  const handleSelect = (document: SearchDocumentResult) => {
    onSelect(document);
    onOpenChange(false);
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
    setDocumentTypeFilter('ALL');
    setSelectedSupplierId(initialSupplierId);
    setSelectedCustomerId(initialCustomerId);
    setPage(0);
  };

  const hasActiveFilters =
    dateFrom ||
    dateTo ||
    amountMin ||
    amountMax ||
    documentTypeFilter !== 'ALL' ||
    (selectedSupplierId && selectedSupplierId !== initialSupplierId) ||
    (selectedCustomerId && selectedCustomerId !== initialCustomerId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[90vw] !max-w-[1400px] h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-2">
            <MagnifyingGlassIcon className="h-5 w-5" />
            Search Purchase Orders & Sales Orders
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 space-y-4">
          {/* Search Input */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by reference number, description, SKU..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                className="pl-9"
                autoFocus
              />
            </div>
            <Button
              variant={showFilters ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-1"
            >
              <FunnelIcon className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                  {[
                    dateFrom,
                    dateTo,
                    amountMin,
                    amountMax,
                    selectedSupplierId &&
                      selectedSupplierId !== initialSupplierId,
                    selectedCustomerId &&
                      selectedCustomerId !== initialCustomerId,
                  ].filter(Boolean).length +
                    (documentTypeFilter !== 'ALL' ? 1 : 0)}
                </Badge>
              )}
            </Button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="border rounded-lg p-4 bg-muted/30 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Filters</span>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-7 text-xs"
                  >
                    <XIcon className="h-3 w-3 mr-1" />
                    Clear all
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Document Type */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Document Type</Label>
                  <Select
                    value={documentTypeFilter}
                    onValueChange={(v) => {
                      setDocumentTypeFilter(v as DocumentTypeFilter);
                      setPage(0);
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All</SelectItem>
                      <SelectItem value="PURCHASE_ORDER">
                        Purchase Orders
                      </SelectItem>
                      <SelectItem value="SALES_ORDER">Sales Orders</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Supplier Filter */}
                {documentTypeFilter !== 'SALES_ORDER' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Supplier</Label>
                    <SupplierAutocomplete
                      supplierId={selectedSupplierId}
                      onSelect={(supplierId) => {
                        setSelectedSupplierId(supplierId);
                        setPage(0);
                      }}
                      className="h-9"
                    />
                  </div>
                )}

                {/* Customer Filter */}
                {documentTypeFilter !== 'PURCHASE_ORDER' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Customer</Label>
                    <CustomersAutocomplete
                      selectedCustomerId={selectedCustomerId}
                      onCustomerSelect={(customerId) => {
                        setSelectedCustomerId(customerId);
                        setPage(0);
                      }}
                      allowClear
                      className="h-9"
                    />
                  </div>
                )}

                {/* Date From */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Date From</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setPage(0);
                    }}
                    className="h-9"
                  />
                </div>

                {/* Date To */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Date To</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setPage(0);
                    }}
                    className="h-9"
                  />
                </div>

                {/* Amount Range */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Amount Range</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={amountMin}
                      onChange={(e) => {
                        setAmountMin(e.target.value);
                        setPage(0);
                      }}
                      className="h-9"
                    />
                    <span className="text-muted-foreground">-</span>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={amountMax}
                      onChange={(e) => {
                        setAmountMax(e.target.value);
                        setPage(0);
                      }}
                      className="h-9"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* Results Table */}
        <div className="flex-1 overflow-auto px-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">Type</TableHead>
                <TableHead>Reference #</TableHead>
                <TableHead>Supplier / Customer</TableHead>
                <TableHead className="w-28">Date</TableHead>
                <TableHead className="w-28 text-right">Amount</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-20 text-center">Charges</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <span className="text-muted-foreground">Searching...</span>
                  </TableCell>
                </TableRow>
              ) : results.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <span className="text-muted-foreground">
                      No documents found
                    </span>
                  </TableCell>
                </TableRow>
              ) : (
                results.map((doc) => {
                  const isPO = doc.documentType === 'PURCHASE_ORDER';
                  const status = isPO ? doc.poStatus : doc.soStatus;
                  const statusLabel = isPO
                    ? (PO_STATUS_LABELS[status ?? ''] ?? status)
                    : (SO_STATUS_LABELS[status ?? ''] ?? status);
                  const statusVariant = isPO
                    ? (PO_STATUS_VARIANTS[status ?? ''] ?? 'outline')
                    : (SO_STATUS_VARIANTS[status ?? ''] ?? 'outline');
                  const partyName = isPO ? doc.supplierName : doc.customerName;

                  return (
                    <TableRow
                      key={doc.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSelect(doc)}
                    >
                      <TableCell>
                        <span
                          className={`text-xs font-semibold ${
                            isPO ? 'text-blue-600' : 'text-green-600'
                          }`}
                        >
                          {isPO ? 'PO' : 'SO'}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">
                        {doc.referenceNumber}
                      </TableCell>
                      <TableCell className="text-muted-foreground truncate max-w-[200px]">
                        {partyName ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateOnlyString(doc.orderDate)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrencyValue(doc.totalAmount, currency)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={statusVariant}
                          className="text-[10px] whitespace-nowrap"
                        >
                          {statusLabel}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {doc.chargesCount && doc.chargesCount > 0 ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] gap-0.5"
                          >
                            <CurrencyDollarIcon className="h-2.5 w-2.5" />
                            {doc.chargesCount}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(doc);
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-3 border-t flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {results.length > 0 ? (
              <>
                Showing {page * pageSize + 1} -{' '}
                {page * pageSize + results.length} of {query.pagination.total}{' '}
                results
                {(excludeDocumentIds?.length ?? 0) > 0 &&
                  results.length !== query.results.length &&
                  ' (some excluded)'}
              </>
            ) : (
              'No results'
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <CaretLeftIcon className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {page + 1} of {Math.max(1, totalPages)}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!query.pagination.hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              <CaretRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
